import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { supabase } from "@/lib/supabase";

/**
 * Rankings data layer (tier-rank tables 0244, audit 0333).
 *
 * Plain table reads/writes under RLS, like the Settings screen: the weights and
 * bands carry no cross-table rule, `tier_rank_*` is staff-read (admin and
 * superadmin) and superadmin-write by column grant, and since 0333 every edit
 * lands in `audit_log`. The page is Super Admin only; the database is the gate.
 */

const numeric = z.coerce.number();

export const TIER_ROLES = ["therapist", "rider"] as const;
export type TierRole = (typeof TIER_ROLES)[number];

export const TIERS = ["bronze", "silver", "gold", "diamond"] as const;
export type Tier = (typeof TIERS)[number];

/** Plain-language names for `tier_metric` — the enum values are not for people. */
export const METRIC_LABEL: Record<string, string> = {
  active_rate: "Active rate",
  platform_usage: "Platform usage",
  client_ratings: "Client ratings",
  client_favorite_index: "Client favorite index",
  dispatch_ratings: "Dispatch ratings",
  dispatch_efficiency: "Dispatch efficiency",
};

const weightSchema = z.object({
  role: z.enum(TIER_ROLES),
  metric: z.string(),
  weight: numeric,
});
const bandSchema = z.object({
  role: z.enum(TIER_ROLES),
  tier: z.enum(TIERS),
  min_score: numeric,
  max_score: numeric,
});
const scoreSchema = z.object({
  staff_id: z.string().uuid(),
  cycle_month: z.string(),
  total_score: numeric,
  tier: z.enum(TIERS),
  is_complete: z.boolean(),
});

export type TierWeight = z.infer<typeof weightSchema>;
export type TierBand = z.infer<typeof bandSchema>;
export type TierScore = z.infer<typeof scoreSchema>;

export function useTierRules() {
  return useQuery({
    queryKey: ["tier-rules"],
    queryFn: async () => {
      const [weights, bands] = await Promise.all([
        supabase.from("tier_rank_weights").select("role, metric, weight").order("metric"),
        supabase.from("tier_rank_bands").select("role, tier, min_score, max_score"),
      ]);
      if (weights.error) throw new Error(weights.error.message);
      if (bands.error) throw new Error(bands.error.message);
      return {
        weights: z.array(weightSchema).parse(weights.data ?? []),
        bands: z.array(bandSchema).parse(bands.data ?? []),
      };
    },
    staleTime: 60_000,
  });
}

export interface TierRulesPatch {
  weights: readonly { role: TierRole; metric: string; weight: number }[];
  bands: readonly { role: TierRole; tier: Tier; min_score: number; max_score: number }[];
}

/**
 * Sequential on purpose, like the distance bands: a refusal should name the row
 * that was refused. `updated_by` is the editing Super Admin — the column grant
 * allows it, and the audit trigger (0333) records `auth.uid()` regardless.
 */
export function useUpdateTierRules() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: TierRulesPatch) => {
      const { data: auth } = await supabase.auth.getUser();
      const meta = { updated_at: new Date().toISOString(), updated_by: auth.user?.id ?? null };
      for (const w of patch.weights) {
        const { error } = await supabase
          .from("tier_rank_weights")
          .update({ weight: w.weight, ...meta })
          .eq("role", w.role)
          .eq("metric", w.metric as never);
        if (error) throw new Error(`${w.role} ${w.metric}: ${error.message}`);
      }
      for (const b of patch.bands) {
        const { error } = await supabase
          .from("tier_rank_bands")
          .update({ min_score: b.min_score, max_score: b.max_score, ...meta })
          .eq("role", b.role)
          .eq("tier", b.tier);
        if (error) throw new Error(`${b.role} ${b.tier}: ${error.message}`);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tier-rules"] }),
  });
}

/**
 * The newest computed score per person. `staff_tier_scores` is one row per
 * person per month; the page wants only the latest, so the rows are read newest
 * first and the first one seen for each person wins.
 */
export function useLatestTierScores() {
  return useQuery({
    queryKey: ["hub-staff", "tier-scores", "latest"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("staff_tier_scores")
        .select("staff_id, cycle_month, total_score, tier, is_complete")
        .order("cycle_month", { ascending: false })
        .limit(5000);
      if (error) throw new Error(error.message);
      const latest = new Map<string, TierScore>();
      for (const row of z.array(scoreSchema).parse(data ?? [])) {
        if (!latest.has(row.staff_id)) latest.set(row.staff_id, row);
      }
      return latest;
    },
    staleTime: 60_000,
  });
}
